import { createSelector } from '@reduxjs/toolkit';

import { getRegionsState, getValidFlavorsByServiceSubtypeName } from 'dux/catalog/selectors';

/**
 * Returns the complete instances state
 *
 * @param {object} state - Current state
 * @returns {object} - The entire instances state
 */
export const getInstancesState = (state) => state.instances;

export const instanceSelector = (state, props) => {
  const { instanceId } = props;
  return getInstancesState(state).instances[instanceId];
};

export const instanceServiceSelector = createSelector(
  instanceSelector,
  (instance) => instance.service,
);

// eslint-disable-next-line max-len
export const getInstanceDatabases = (state) => (instanceId) =>
  getInstancesState(state).instances[instanceId].databases;

// eslint-disable-next-line max-len
export const getInstanceSpaceUsage = (state) => (instanceId) =>
  getInstancesState(state).instances[instanceId].spaceUsage;

export const getInstanceTotalSpace = (state) => (instanceId) => {
  const instance = getInstancesState(state).instances[instanceId];
  let totalSpace = 0;

  instance.hosts.forEach((host) => {
    if (host.role === instance.service.toLowerCase()) {
      if (instance.service === 'MongoDB') {
        totalSpace = host.diskSizeGb;
      } else {
        totalSpace += host.diskSizeGb * host.quantity;
      }
    }
  });

  return totalSpace;
};

export const instanceDatabaseIsLoadingSelector = createSelector(
  getInstancesState,
  (instanceState) => instanceState.instanceDatabaseIsLoading,
);

// eslint-disable-next-line max-len
export const getPrimaryCertId = (state) => (instanceId) =>
  getInstancesState(state).instances[instanceId].certificates.filter(
    (certificate) => certificate.primary,
  )[0].id;

// eslint-disable-next-line max-len
export const getCerts = (state) => (instanceId) =>
  getInstancesState(state).instances[instanceId].certificates;

// eslint-disable-next-line max-len
export const getClientCerts = (state) => (instanceId) =>
  getInstancesState(state).instances[instanceId].clientCerts;

export const getCockroachdbMultiInstanceIDs = (state) => {
  const instancesArray = Object.values(getInstancesState(state).instances);
  const crdbidsArray = instancesArray
    .filter((instance) => instance.service.toLowerCase() === 'cockroachdb')
    .map((instance) => instance.metadata.multiInstanceId);
  const uniqueIds = [...new Set(crdbidsArray)];
  return uniqueIds;
};

// eslint-disable-next-line max-len
export const getAssociatedCockroachDBInstances = (state) => (
  multiInstanceId,
  currentInstanceId,
) => {
  const { instances } = getInstancesState(state);
  const associatedInstances = Object.values(instances).filter(
    (instance) =>
      typeof instance.metadata.multiInstanceId !== 'undefined' &&
      instance.metadata.multiInstanceId === multiInstanceId &&
      instance.id !== currentInstanceId,
  );
  return associatedInstances;
};

// eslint-disable-next-line max-len
export const getInstanceCertificatesState = (state) => (instanceId) =>
  getInstancesState(state).instances[instanceId].certificates;

export const getRedisMemoryCapacity = (state) => (instanceId) => {
  const memorySize = getInstancesState(state).instances[instanceId].hosts[0].memoryMb;
  return memorySize % 1024 === 0 ? memorySize / 1024 : (memorySize / 1024).toFixed(1);
};

export const getUnusedCockroachDBRegions = (state) => (instanceId) => {
  const { instances } = getInstancesState(state);
  const currentInstance = instances[instanceId];
  const {
    region,
    metadata: { multiInstanceId },
  } = currentInstance;
  const clusterInstances = getAssociatedCockroachDBInstances(state)(multiInstanceId, instanceId);

  let usedRegions = clusterInstances.map((clusterInstance) => clusterInstance.region);
  usedRegions = [region, ...usedRegions];

  const { regions: allRegions } = getRegionsState(state);

  const remainingRegions = allRegions.filter(
    (aRegion) =>
      !usedRegions.includes(aRegion.name) && aRegion.providerName === currentInstance.provider,
  );
  return remainingRegions;
};

export const getUsedCockroachDBRegions = (state) => (instanceId) => {
  const { instances } = getInstancesState(state);
  const currentInstance = instances[instanceId];
  const {
    region,
    metadata: { multiInstanceId },
  } = currentInstance;
  const clusterInstances = getAssociatedCockroachDBInstances(state)(multiInstanceId, instanceId);

  let usedRegions = clusterInstances.map((clusterInstance) => clusterInstance.region);
  usedRegions = [region, ...usedRegions];
  return usedRegions;
};

export const getServiceSubtypeNameByInstanceId = (state) => (instanceId) => {
  const { instances } = getInstancesState(state);
  return instances[instanceId].serviceSubtype;
};

export const getValidFlavorsByInstanceId = (state) => (instanceId) => {
  const serviceSubtypeName = getServiceSubtypeNameByInstanceId(state)(instanceId);
  const validFlavors = getValidFlavorsByServiceSubtypeName(state)(serviceSubtypeName);
  return validFlavors;
};

export default getInstancesState;
